import * as React from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { request } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CloudAccountAwsConfig } from "../../../schema/types/cloudaccount/entity";

export const AWSSummary: React.FC = () => {
  const navigate = useNavigate();
  const basePath = React.useContext(BasePathContext);
  const theme = useAppSelector((state) => state.theme.theme);

  const isEnabled = useCpFormValue("aws.isEnabled");
  const cloudAccount = useCpFormValue("aws.cloudAccount");
  const method = useCpFormValue("aws.method");
  const roleName = useCpFormValue("aws.roleName");
  const policyRefs = useCpFormValue("aws.policyRefs");

  const awsLogoPath = `/resources/logos/awsLogo${theme === "dark" ? "Dark" : ""}.png`;

  const [cloudAccountData, setCloudAccountData] = React.useState<CloudAccountAwsConfig | null>(null);
  const [isLoadingCA, setIsLoadingCA] = React.useState(false);

  React.useEffect(() => {
    if (isEnabled && cloudAccount.link) {
      fetchCloudAccount();
    } else {
      setCloudAccountData(null);
    }
  }, [isEnabled, cloudAccount.link]);

  async function fetchCloudAccount() {
    try {
      setIsLoadingCA(true);
      const res = await request({ url: cloudAccount.link });
      setCloudAccountData(res.data?.data as CloudAccountAwsConfig);
    } catch {
      setCloudAccountData(null);
    } finally {
      setIsLoadingCA(false);
    }
  }

  function handleClickLogo() {
    navigate(`${basePath}/cloud-access-aws`);
  }

  if (!isEnabled) {
    return (
      <button
        onClick={handleClickLogo}
        style={{
          width: "100%",
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--identity-aws-logo-drop)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius)",
          cursor: "pointer",
          transition: "box-shadow var(--transition)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
      >
        <img src={awsLogoPath} alt="AWS" style={{ height: 36 }} />
      </button>
    );
  }

  const caName = cloudAccount.value || parseLink(cloudAccount.link, { useInputCtx: true }).name;
  const roleArn = cloudAccountData?.roleArn;

  const policies =
    policyRefs.inputMode === "selection"
      ? policyRefs.selectedPolicies
      : policyRefs.manualPolicies?.map((p: any) => p.value) || [];

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "4px solid var(--identity-aws-logo-accent)",
        }}
      >
        <img src={awsLogoPath} alt="AWS" style={{ height: 36 }} />
      </div>

      <div style={{ padding: "20px" }}>
        {isLoadingCA ? (
          <div className="flex justify-center py-4">
            <Spin />
          </div>
        ) : (
          <>
            <div className="flex mb-4">
              <div style={{ width: "33.33%" }}>
                <FormLabel>Cloud Account</FormLabel>
                {cloudAccount.link ? (
                  <a
                    href={`/console${cloudAccount.link}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/console${cloudAccount.link}`);
                    }}
                    className="color-link"
                  >
                    {caName}
                  </a>
                ) : (
                  <div>{caName}</div>
                )}
              </div>
              <div style={{ width: "66.67%" }}>
                <FormLabel>Role ARN</FormLabel>
                <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{roleArn || "-"}</div>
              </div>
            </div>

            {method === "role" && roleName.value ? (
              <div className="mb-4">
                <FormLabel>Selected AWS Role</FormLabel>
                <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{roleName.value}</div>
              </div>
            ) : null}

            {method === "policies" && policies.length > 0 ? (
              <div className="mb-4">
                <FormLabel>Selected AWS Policies</FormLabel>
                <ul style={{ margin: 0, paddingLeft: 20, listStyleType: "disc" }}>
                  {policies.map((p: string, i: number) => (
                    <li key={i} style={{ fontFamily: "var(--font-mono, monospace)" }}>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        <div className="flex justify-end mt-4">
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate(`${basePath}/cloud-access-aws`);
            }}
            className="color-link"
            style={{ cursor: "pointer", fontWeight: 500 }}
          >
            Details →
          </a>
        </div>
      </div>
    </div>
  );
};
